/**
 * THE API SURFACE — one versioned contract, consumed by web AND native.
 *
 * OWNER: app-architect (paths, methods, guards). backend-engineer owns the
 * request/response BODY types below and may extend them; a path, method or guard
 * change is arbitrated.
 *
 * Every id here maps 1:1 to FLOWS_SPEC §12.1's API interaction register.
 *
 * ENDPOINTS THAT MUST NOT EXIST — a positive contract (FLOWS_SPEC §12.1):
 *   · any measurement update or delete, for any role, at any path (D-009).
 *     Enforced by ABSENCE, not by a 403. A guarded PUT is a misconfiguration
 *     waiting to happen; a route that does not exist cannot be misconfigured.
 *   · any client-reachable override path (P-CFG-04).
 *   · any direct write to `orders.state` (OPS_SPEC §2.6 — transitions only).
 *   · any client-supplied price, actor id, role claim or event timestamp.
 */

import type {
  AcceptedAcknowledgement,
  Configuration,
  ConstraintWarning,
  HaltCode,
  MeasurementRecord,
  NotApplicableOption,
  OptionState,
  OrderState,
  OrderTransitionEvent,
  PromisedWindow,
  Quote,
  Role,
  StockBand,
  StockMovementReason,
} from './domain.ts';

export const API_VERSION = 'v1' as const;
export const API_PREFIX = `/api/${API_VERSION}` as const;

export type HttpMethod = 'GET' | 'POST' | 'DELETE';

/** `null` = no session required. Otherwise the roles that may reach the route. */
export type RoleGuard = readonly Role[] | null;

export interface EndpointSpec {
  /** FLOWS_SPEC §12.1 id. */
  readonly id: string;
  readonly method: HttpMethod;
  /** Path relative to API_PREFIX. `:param` segments are opaque ids or an orderRef. */
  readonly path: string;
  readonly auth: RoleGuard;
  /** Written down so the audit requirement cannot be forgotten (D-016). */
  readonly logsMeasurementRead: boolean;
  readonly note?: string;
}

const CLIENT: readonly Role[] = ['client'];
const STAFF: readonly Role[] = ['master_tailor', 'admin', 'production', 'fitter'];
const BACKOFFICE: readonly Role[] = ['master_tailor', 'admin'];
const TAILOR: readonly Role[] = ['master_tailor'];

export const ENDPOINTS: readonly EndpointSpec[] = [
  /* ------------------------------------------------------------ platform */
  { id: 'API-HEALTH', method: 'GET', path: '/health', auth: null, logsMeasurementRead: false },

  /* ------------------------------------------------------------- session */
  {
    id: 'API-SESSION-SIGNIN',
    method: 'POST',
    path: '/session',
    auth: null,
    logsMeasurementRead: false,
    note: 'Identical response shape AND timing for a known and an unknown identifier ([T-14]). Destination decided server-side from the role (FLOWS_SPEC §1.1).',
  },
  { id: 'API-SESSION-CURRENT', method: 'GET', path: '/session', auth: [...CLIENT, ...STAFF], logsMeasurementRead: false },
  { id: 'API-SESSION-SIGNOUT', method: 'DELETE', path: '/session', auth: [...CLIENT, ...STAFF], logsMeasurementRead: false },
  { id: 'API-PASSWORD-RESET', method: 'POST', path: '/password-reset', auth: null, logsMeasurementRead: false, note: 'Identical response either way.' },

  /* -------------------------------------------------------- client rooms */
  { id: 'API-HOME', method: 'GET', path: '/home', auth: CLIENT, logsMeasurementRead: false },
  {
    id: 'API-MEAS-LIST',
    method: 'GET',
    path: '/measurements',
    auth: CLIENT,
    logsMeasurementRead: true,
    note: 'Own records only. clientId comes from the session, never from a param.',
  },
  { id: 'API-MEAS-GET', method: 'GET', path: '/measurements/:recordId', auth: CLIENT, logsMeasurementRead: true, note: "Another client's recordId returns the ST-403 shape — identical to 'does not exist'." },
  {
    id: 'API-REMEASURE-CREATE',
    method: 'POST',
    path: '/remeasure-requests',
    auth: CLIENT,
    logsMeasurementRead: false,
    note: 'P-MEAS-05: rejects the WHOLE request if any measurement field id appears at any depth.',
  },
  { id: 'API-PRIVACY-REQUEST', method: 'POST', path: '/privacy-requests', auth: CLIENT, logsMeasurementRead: false },

  /* ------------------------------------------------------------ the book */
  { id: 'API-FABRICS-LIST', method: 'GET', path: '/fabrics', auth: CLIENT, logsMeasurementRead: false, note: 'tier, availability, requirement, price and selectability are ALL server-computed.' },
  { id: 'API-FABRIC-GET', method: 'GET', path: '/fabrics/:clothId', auth: CLIENT, logsMeasurementRead: false },
  { id: 'API-SWATCH-CREATE', method: 'POST', path: '/swatch-requests', auth: CLIENT, logsMeasurementRead: false },
  { id: 'API-CLOTH-INTEREST-CREATE', method: 'POST', path: '/cloth-interests', auth: CLIENT, logsMeasurementRead: false },

  /* ----------------------------------------------------- the commission */
  { id: 'API-DRAFT-CREATE', method: 'POST', path: '/drafts', auth: CLIENT, logsMeasurementRead: false },
  { id: 'API-DRAFT-GET', method: 'GET', path: '/drafts/:draftId', auth: CLIENT, logsMeasurementRead: false },
  { id: 'API-DRAFT-DELETE', method: 'DELETE', path: '/drafts/:draftId', auth: CLIENT, logsMeasurementRead: false, note: 'Delete takes the same number of actions as create (B24).' },
  {
    id: 'API-CONFIG-EVAL',
    method: 'POST',
    path: '/drafts/:draftId/evaluate',
    auth: CLIENT,
    logsMeasurementRead: false,
    note: 'THE authoritative call. The UI never computes selectability, price, metreage or a rule outcome.',
  },
  { id: 'API-REVIEW-GET', method: 'GET', path: '/drafts/:draftId/review', auth: CLIENT, logsMeasurementRead: false, note: 'Always takes its OWN fresh quote; never trusts the configurator’s.' },
  {
    id: 'API-ORDER-PLACE',
    method: 'POST',
    path: '/orders',
    auth: CLIENT,
    logsMeasurementRead: false,
    note: 'No price field is ever sent (P-ORD-02). One that is, is ignored and logged as a tamper signal.',
  },
  { id: 'API-ORDER-LIST', method: 'GET', path: '/orders', auth: CLIENT, logsMeasurementRead: false },
  { id: 'API-ORDER-GET', method: 'GET', path: '/orders/:orderRef', auth: CLIENT, logsMeasurementRead: false, note: 'Client-safe projection only. No metreage, bolt, halt code or measurement value.' },
  { id: 'API-ORDER-REQUEST-CREATE', method: 'POST', path: '/orders/:orderRef/requests', auth: CLIENT, logsMeasurementRead: false, note: 'A recorded request routed to a named person. NOT a state transition (D-030).' },

  /* ------------------------------------------------------- the site's wire
   * WIRE-1 (7 Sept 2026). A commission placed on the website arrives here as a
   * LEAD. The intake is public in the sense that no session reaches it, and
   * closed in the sense that only the site's own server holds the key it
   * presents (`x-pt-lead-key`, PT_LEAD_KEY). It carries no measurement — the
   * tripwire refuses the whole request if it tries (P-MEAS-05). */
  {
    id: 'API-LEAD-CREATE',
    method: 'POST',
    path: '/leads',
    auth: null,
    logsMeasurementRead: false,
    note: "No session: the site's server presents PT_LEAD_KEY in x-pt-lead-key. Refused as NOT_AUTHENTICATED without it. A repeated site reference is refused, not stored twice.",
  },
  { id: 'API-ATELIER-LEADS-LIST', method: 'GET', path: '/atelier/leads', auth: BACKOFFICE, logsMeasurementRead: false, note: '?status=new (default) | client | closed | all.' },
  { id: 'API-ATELIER-LEAD-GET', method: 'GET', path: '/atelier/leads/:leadId', auth: BACKOFFICE, logsMeasurementRead: false },
  {
    id: 'API-ATELIER-LEAD-CLIENT',
    method: 'POST',
    path: '/atelier/leads/:leadId/client',
    auth: BACKOFFICE,
    logsMeasurementRead: false,
    note: 'Makes a client record from the lead (P-CLIENT-01), or attaches it to an existing one when the body names a clientId. Either way the specification lands on his record as a note.',
  },
  { id: 'API-ATELIER-LEAD-CLOSE', method: 'POST', path: '/atelier/leads/:leadId/close', auth: BACKOFFICE, logsMeasurementRead: false, note: 'With a reason. Nothing is deleted.' },

  /* --------------------------------------------------------- back office */
  { id: 'API-ATELIER-ORDERS-LIST', method: 'GET', path: '/atelier/orders', auth: STAFF, logsMeasurementRead: false, note: '`needs` and sort computed server-side; the UI holds no copy of the state machine.' },
  { id: 'API-ATELIER-ORDER-GET', method: 'GET', path: '/atelier/orders/:orderRef', auth: STAFF, logsMeasurementRead: true, note: 'Carries the frozen cut sheet, the appendix and allowedEvents FOR THIS ROLE.' },
  {
    id: 'API-ATELIER-TRANSITION',
    method: 'POST',
    path: '/atelier/orders/:orderId/transitions',
    auth: STAFF,
    logsMeasurementRead: false,
    note: 'THE ONLY write path to order state. Carries expectedFrom and an idempotencyKey.',
  },
  { id: 'API-ATELIER-NOTE', method: 'POST', path: '/atelier/orders/:orderId/notes', auth: STAFF, logsMeasurementRead: false },
  { id: 'API-ATELIER-ASSIGN', method: 'POST', path: '/atelier/orders/:orderId/assignment', auth: BACKOFFICE, logsMeasurementRead: false },
  {
    id: 'API-ATELIER-REPROMISE',
    method: 'POST',
    path: '/atelier/orders/:orderId/promise',
    auth: BACKOFFICE,
    logsMeasurementRead: false,
    note: 'order.promise / order.repromise. The ONLY mechanism by which a date reaches a client (D-026(d)). Reason required.',
  },
  { id: 'API-ATELIER-CUTSHEET-PRINT', method: 'POST', path: '/atelier/orders/:orderId/cutsheet-prints', auth: STAFF, logsMeasurementRead: true },
  { id: 'API-ATELIER-CLIENTS-LIST', method: 'GET', path: '/atelier/clients', auth: BACKOFFICE, logsMeasurementRead: false },
  /* ------------------------------------------------- P-CLIENT-01, migration 006
   * LAW 31: "Paul needs to be able to set up client profiles… create a record
   * for each client that has all their information including shipping address
   * etc which can later be edited by the client."
   *
   * Note what is here and what is not. There is a CREATE, an AMEND and a NOTE,
   * and there is NO DELETE — at any path, for any role. Erasure is rule 7's
   * crypto-shred, which destroys the key rather than the row, because an
   * append-only ledger that can be deleted is not one. The amendment is a POST
   * to its own sub-resource rather than a PUT on the client, because what is
   * being created IS a record: one field, one reason, one row in the ledger. */
  {
    id: 'API-ATELIER-CLIENT-CREATE',
    method: 'POST',
    path: '/atelier/clients',
    auth: BACKOFFICE,
    logsMeasurementRead: false,
    note: 'The account is created with NO CREDENTIAL — the portal is phase 3. An email is optional; without one the identifier is at .invalid and the payload says so.',
  },
  {
    id: 'API-ATELIER-CLIENT-AMEND',
    method: 'POST',
    path: '/atelier/clients/:clientId/amendments',
    auth: BACKOFFICE,
    logsMeasurementRead: false,
    note: 'ONE FIELD PER CALL, with what it was. A bulk amendment writes one ledger row for three changes and the reason attaches to whichever the reader guesses.',
  },
  {
    id: 'API-ATELIER-CLIENT-NOTE',
    method: 'POST',
    path: '/atelier/clients/:clientId/notes',
    auth: BACKOFFICE,
    logsMeasurementRead: false,
    note: 'Append-only. A correction is a NEW note. Atelier-internal: a client note does not cross to the workshop.',
  },
  { id: 'API-ATELIER-CLIENTS-GET', method: 'GET', path: '/atelier/clients/:clientId', auth: BACKOFFICE, logsMeasurementRead: false },
  {
    id: 'API-ATELIER-MEAS-LIST',
    method: 'GET',
    path: '/atelier/clients/:clientId/measurement-records',
    auth: BACKOFFICE,
    logsMeasurementRead: true,
    note: 'Every open is audit-logged and the page says so ([T-78]).',
  },
  /* -------------------------------------------------- P-ORDER-STAFF-01
   * LAW 31: "We have paul going from city to city office to office measuring
   * and people placing orders in person looking at physical swatches"… "and he
   * needs to be able to order it and charge the customer on the spot".
   *
   * PLATFORM-1 found the other half of the same hole P-CLIENT-01 closed: the
   * order route takes CLIENTS, not staff. `API-ORDER-PLACE` is `clientScope`d,
   * so the only person who could ever place an order was the man being measured
   * — sitting at home, on a portal that does not exist yet.
   *
   * TAILOR, not BACKOFFICE, and it is the same reasoning as
   * `API-ATELIER-MEAS-CREATE` below: placing a commission on somebody else's
   * behalf means attesting that he was shown what he was shown, and it carries
   * the power to release a rule. That is the master tailor's, not an
   * administrator's. */
  {
    id: 'API-ATELIER-ORDER-PLACE',
    method: 'POST',
    path: '/atelier/clients/:clientId/orders',
    auth: TAILOR,
    logsMeasurementRead: true,
    note: 'Placing on the client’s behalf. Every acknowledgement must be ENUMERATED by the tailor — the server refuses if one the client should have seen is missing — and each is stamped with WHO showed it. No price is ever read from the body, on this path or any other.',
  },
  {
    id: 'API-ATELIER-MEAS-CREATE',
    method: 'POST',
    path: '/atelier/clients/:clientId/measurement-records',
    auth: TAILOR,
    logsMeasurementRead: false,
    note: 'CREATE ONLY. There is no PUT, PATCH or DELETE counterpart at any path, for any role (D-009, P-MEAS-06).',
  },
  { id: 'API-ATELIER-MEAS-LOCK', method: 'POST', path: '/atelier/measurement-records/:recordId/lock', auth: TAILOR, logsMeasurementRead: false, note: 'draft -> locked. The system then sets the predecessor to superseded. No value changes.' },
  /* ------------------------------------------------- P-PHOTO-01, migration 005
   * LAW 31: "the manufacturer sees pictures of the customer also because paul
   * takes 3 pictures". OWNER RULING OR-2: they are a CONSTRUCTION INPUT — they
   * decide the shoulder treatment, the stance and the balance of the coat.
   *
   * Note the shape of this group, which is the ruling made structural. There is
   * NO update route and NO delete route, at any path, for any role — a set is
   * append-only and a re-take is a NEW set (OR-2: "the most recent governs, the
   * previous is kept for comparison"). It is the same absence D-009 makes for
   * measurements, provable the same way: by enumerating this list. */
  {
    id: 'API-ATELIER-CONSENT-RECORD',
    method: 'POST',
    path: '/atelier/clients/:clientId/consents',
    auth: TAILOR,
    logsMeasurementRead: false,
    note: 'OR-4: consent is taken ONCE, in plain words, at the appointment. The WORDS ARE PAUL\u2019S and are stored verbatim; nothing here composes them.',
  },
  {
    id: 'API-ATELIER-PHOTOSET-CREATE',
    method: 'POST',
    path: '/atelier/clients/:clientId/photograph-sets',
    auth: TAILOR,
    logsMeasurementRead: false,
    note: 'CREATE ONLY, and the SET is the unit \u2014 written whole in one transaction or not at all. Refuses until a consent naming where the photographs go is on file (OR-1).',
  },
  {
    id: 'API-ATELIER-PHOTOSET-LIST',
    method: 'GET',
    path: '/atelier/clients/:clientId/photograph-sets',
    auth: BACKOFFICE,
    logsMeasurementRead: true,
    note: 'Metadata, never bytes. The HISTORY is the atelier\u2019s; the workshop is shown the governing set on the work order and nothing else.',
  },
  {
    id: 'API-ATELIER-PHOTOGRAPH-GET',
    method: 'GET',
    path: '/atelier/photographs/:photographId',
    auth: STAFF,
    logsMeasurementRead: true,
    note: 'The bytes. STAFF because OR-1 sends the photographs to Thailand \u2014 production is scoped to clients with an order on its own bench. Every open is logged with the reader\u2019s name.',
  },
  { id: 'API-ATELIER-MEAS-VERIFY', method: 'POST', path: '/atelier/measurement-records/:recordId/verification', auth: TAILOR, logsMeasurementRead: false, note: 'Sets verifiedBy on a legacy record (P-MEAS-03). Changes no measurement value.' },
  /* ----------------------------------------------------- P-PAY-01, migration 007
   * LAW 31: "he needs to be able to order it and charge the customer on the
   * spot". OWNER RULING OR-3: "the api should be able to set up to accept any
   * payment processor."
   *
   * BACKOFFICE, NOT STAFF, on every one of them — and that is law 31(c) in the
   * register rather than in a projection. `production` and `fitter` do not
   * handle money and are not shown that money exists. The price-blind guard
   * (apps/server/src/middleware/price-blind.ts) is the backstop; this is the
   * front stop, and a route the factory cannot reach is a route whose payload
   * nobody has to remember to strip. */
  {
    id: 'API-ATELIER-PAYMENT-METHODS',
    method: 'GET',
    path: '/atelier/payment-methods',
    auth: BACKOFFICE,
    logsMeasurementRead: false,
    note: 'Which adapters exist and which are usable RIGHT NOW, each with the reason. A payment path that fails while a client is holding out a card is worse than one never offered.',
  },
  {
    id: 'API-ATELIER-PAYMENT-CREATE',
    method: 'POST',
    path: '/atelier/orders/:orderId/payments',
    auth: BACKOFFICE,
    logsMeasurementRead: false,
    note: 'An intent: this much, for this reason, through this adapter. The amount is a number a person typed — there is no default deposit anywhere in this codebase (decision 4 is unanswered).',
  },
  {
    id: 'API-ATELIER-PAYMENTS-LIST',
    method: 'GET',
    path: '/atelier/orders/:orderId/payments',
    auth: BACKOFFICE,
    logsMeasurementRead: false,
    note: 'What has been taken and what is left, by arithmetic on the ledger. Not a schedule and not a policy.',
  },
  {
    id: 'API-ATELIER-PAYMENT-EVENT',
    method: 'POST',
    path: '/atelier/payments/:paymentIntentId/events',
    auth: BACKOFFICE,
    logsMeasurementRead: false,
    note: 'APPEND ONLY. Capture, refund, cancel, fail or reconcile — there is no PUT and no DELETE at any path, for any role. A correction is a new event.',
  },
  /* ------------------------------------------------------ P-THAI-01, migration 009
   * LAW 31: "then that crm will show the suit data to the manufacturer in both
   * english and in thai". PLATFORM-1 decision 6's recorded default: a named
   * bilingual person, recorded per term; untranslated prints English with a
   * marker.
   *
   * BACKOFFICE on both, and the reason is worth stating: the workshop READS the
   * Thai on its work order — it does not maintain the glossary. Signing a term
   * is an attestation by a named person that this is what the house means, and
   * it belongs where the house is, not where the suits are made. The bilingual
   * person gets a back-office login. */
  {
    id: 'API-ATELIER-RETENTION',
    method: 'GET',
    path: '/atelier/retention',
    auth: BACKOFFICE,
    logsMeasurementRead: false,
    note: 'What the declared retention schedule WOULD delete. It reports; it deletes nothing and schedules nothing, and the payload says so — SECURITY_NOTES §4 item 4 is half closed and the half is named.',
  },
  {
    id: 'API-ATELIER-GLOSSARY-LIST',
    method: 'GET',
    path: '/atelier/glossary',
    auth: BACKOFFICE,
    logsMeasurementRead: false,
    note: 'Every term a work order can print, derived from rules.json. `?status=unsigned` is the queue of what still needs a bilingual reader.',
  },
  /* ------------------------------------------------- LAW 59, migration 013
   * The owner (18 October 2026): "build the cut tickets into pauls backend so he
   * could do this on ipad … make it so paul can write using apple pen and sketch
   * if needed." The tickets themselves ride on API-ATELIER-ORDER-GET (`tickets`),
   * projected from the frozen sheet; this is the one write: Paul's own hand on a
   * ticket, kept as strokes. APPEND ONLY, like a photograph set and a glossary
   * signature: a new drawing is a new row, the old one is what was on the tickets
   * already printed. BACKOFFICE draws; the workshop reads it on the order. */
  {
    id: 'API-ATELIER-TICKETS-GET',
    method: 'GET',
    path: '/atelier/orders/:orderRef/tickets',
    auth: STAFF,
    logsMeasurementRead: true,
    note: 'The cut tickets, one a garment, projected from the frozen sheet, with the governing photograph set that prints with them. Read apart from the order so an order\u2019s own screen does not carry every drawing. STAFF: the workshop reads the tickets it staples to the cloth; the price-blind guard stands behind it.',
  },
  {
    id: 'API-ATELIER-TICKET-SKETCH-CREATE',
    method: 'POST',
    path: '/atelier/orders/:orderId/tickets/:garment/sketches',
    auth: BACKOFFICE,
    logsMeasurementRead: false,
    note: 'APPEND ONLY. Strokes, not pixels, so the drawing prints at any size; capped in size and count. The garment must be one the order\u2019s sheet carries. The latest drawing a garment comes back on API-ATELIER-TICKETS-GET.',
  },
  {
    id: 'API-ATELIER-GLOSSARY-SIGN',
    method: 'POST',
    path: '/atelier/glossary/:termKey/signatures',
    auth: BACKOFFICE,
    logsMeasurementRead: false,
    note: 'APPEND ONLY. A better translation is a NEW signature — the old one is what was on the sheets already printed. Signed against the English of the day; when the English moves, the term goes stale and prints English again.',
  },
  { id: 'API-ATELIER-CLOTH-LIST', method: 'GET', path: '/atelier/cloths', auth: STAFF, logsMeasurementRead: false },
  { id: 'API-ATELIER-CLOTH-GET', method: 'GET', path: '/atelier/cloths/:clothId', auth: STAFF, logsMeasurementRead: false, note: 'Includes the movement ledger, newest last, with a running balance.' },
  {
    id: 'API-ATELIER-STOCK-MOVE',
    method: 'POST',
    path: '/atelier/cloths/:clothId/stock-movements',
    auth: BACKOFFICE,
    logsMeasurementRead: false,
    note: 'A SIGNED DELTA ONLY. No endpoint anywhere accepts an absolute on-hand quantity.',
  },
  { id: 'API-ATELIER-CLOTH-LIFECYCLE', method: 'POST', path: '/atelier/cloths/:clothId/lifecycle', auth: BACKOFFICE, logsMeasurementRead: false, note: 'publish / unpublish / discontinue. Discontinue requires a reason.' },
  { id: 'API-ATELIER-SWATCHES-LIST', method: 'GET', path: '/atelier/swatch-requests', auth: BACKOFFICE, logsMeasurementRead: false },
  { id: 'API-ATELIER-SWATCHES-MARKSENT', method: 'POST', path: '/atelier/swatch-requests/:id/sent', auth: BACKOFFICE, logsMeasurementRead: false },
  { id: 'API-ATELIER-CLOTH-INTERESTS', method: 'GET', path: '/atelier/cloth-interests', auth: BACKOFFICE, logsMeasurementRead: false },
];

/* ---------------------------------------------------------------- leads */

/** WIRE-1. Three words, no status board: `new` is the queue. */
export type LeadStatus = 'new' | 'client' | 'closed';

/** What the site sends. `specification` is the site's own record, stored as sent. */
export interface LeadCreateRequest {
  readonly reference: string;
  readonly receivedAt?: string;
  readonly placedFrom?: string;
  readonly unmeasured: boolean;
  readonly contact: { readonly name: string; readonly email?: string; readonly phone?: string };
  readonly specification: Record<string, unknown>;
}

export interface LeadRow {
  readonly leadId: string;
  readonly siteReference: string;
  readonly receivedAt: string;
  readonly contactName: string;
  readonly contactEmail: string | null;
  readonly contactPhone: string | null;
  readonly unmeasured: boolean;
  readonly garment: string | null;
  readonly clothCode: string | null;
  readonly clothName: string | null;
  readonly status: LeadStatus;
  readonly clientId: string | null;
  readonly clientRef: string | null;
  readonly handledByName: string | null;
  readonly handledAt: string | null;
  readonly closedReason: string | null;
}

/**
 * AMENDED 18 October 2026, LAW 58 ("at check out i need it to give the actual
 * product skews and color codes etc for swatches of fabric and books ... and
 * then we need to create a backend where paul has it"). One cloth a commission
 * names, with the mill's references as the site sent them: the suit's cloth,
 * its lining, or one shirt. Every field is a word the site sent, or null when it
 * sent none; nothing is looked up or inferred. A piece the site named with no
 * book, no code and no colour carries no reference and is not listed.
 */
export interface LeadReference {
  readonly part: 'cloth' | 'lining' | 'shirt';
  readonly name: string | null;
  /** The pattern book, as the site names it. */
  readonly book: string | null;
  /** The mill's code for the cloth. */
  readonly code: string | null;
  /** The colour's word, as the site names it. */
  readonly shade: string | null;
  /** '#' and three or six hex digits, lower case; null when none was sent, or what was sent is not a colour code. */
  readonly hex: string | null;
  /** The cloth only. */
  readonly weight: string | null;
  readonly fibre: string | null;
  readonly weave: string | null;
  /** Shirts only: how many of this one. */
  readonly qty: number | null;
}

export interface LeadDetail extends LeadRow {
  readonly placedFrom: string | null;
  readonly specification: Record<string, unknown>;
  /** The words the CRM read out of the specification, one line each, for the tailor. */
  readonly summary: readonly { readonly label: string; readonly value: string }[];
  /** AMENDED 18 October 2026, LAW 58: the mill's references, read out of the same specification. */
  readonly references: readonly LeadReference[];
}

export const ENDPOINT_COUNT = ENDPOINTS.length;

export function endpoint(id: string): EndpointSpec {
  const found = ENDPOINTS.find((e) => e.id === id);
  if (!found) throw new Error(`Unknown endpoint id: ${id}`);
  return found;
}

/* ==========================================================================
 * Request / response bodies. Owned by backend-engineer; extend freely,
 * rename never.
 * ======================================================================== */

export interface SignInRequest {
  readonly identifier: string;
  readonly credential: string;
}

export interface SessionResponse {
  readonly role: Role;
  readonly displayName: string;
  /** Where the server says this role lands. Never a form field, never a URL (FLOWS_SPEC §1.1). */
  readonly landing: '/wardrobe' | '/atelier/orders' | '/atelier/production';
  /** Native only: the bearer token. Web receives an httpOnly cookie and `token` is null. */
  readonly token: string | null;
  /**
   * LAW 60 · true on the demonstration office only: every person there is a placeholder and
   * every order a fiction, and the shell says so on every screen. Absent or false on the real one.
   */
  readonly demonstration?: boolean;
}

export interface HomeResponse {
  readonly client: { readonly displayName: string };
  /** [T-16] — the name of the master tailor who holds his record. */
  readonly tailor: { readonly displayName: string; readonly role: string } | null;
  readonly draft: { readonly draftId: string; readonly lastTouchedAt: string; readonly stale: boolean } | null;
  readonly wardrobe: readonly Record<string, unknown>[];
  readonly orders: readonly Record<string, unknown>[];
  readonly hasCurrentRecord: boolean;
}

export interface MeasurementListResponse {
  readonly records: readonly MeasurementRecord[];
  readonly currentRecordId: string | null;
  /** ST-MEAS-LASTOPENED. Null iff the access log is not shipped — the claim is then DROPPED, not softened. */
  readonly lastOpened: { readonly byName: string; readonly at: string } | null;
}

export interface FabricListQuery {
  readonly season?: string;
  readonly occasion?: string;
  readonly weave?: string;
  readonly colour?: string;
  readonly sort?: 'house' | 'weight' | 'price_asc' | 'price_desc';
  readonly draftId?: string;
  readonly includeTier0?: boolean;
}

export interface ConfigEvalRequest {
  /** Only the options the client changed; the server holds the draft. */
  readonly configuration: Configuration;
}

export interface ConfigEvalResponse {
  readonly options: readonly OptionState[];
  readonly notApplicable: readonly NotApplicableOption[];
  readonly warnings: readonly ConstraintWarning[];
  readonly acknowledgementsPending: readonly string[];
  readonly missingRequired: readonly string[];
  readonly quote: Quote | null;
  readonly clothContext: {
    readonly clothId: string;
    /**
     * The cloth's name, so the one authoritative call can NAME the cloth it is
     * about. Without it a screen holding a commission has to fetch
     * `/fabrics/:clothId` purely to render a label — a second round trip that
     * can fail or lag, and whose failure mode is a cloth id on screen where a
     * name belongs. It is already on every card (`CLIENT_CLOTH_FIELDS`), so it
     * discloses nothing new.
     */
    readonly clothName: string;
    readonly availableMetres: number;
    readonly requirementM: number;
    readonly stockBand: StockBand;
    readonly selectable: boolean;
    readonly reasonCode: string | null;
  } | null;
}

/**
 * What the client SUBMITS when he accepts an acknowledgement — deliberately a
 * different type from `AcceptedAcknowledgement`, which is the STORED record.
 *
 * The stored type keeps `deltaEBasis` optional so rows written before rules
 * 1.3.2 stay readable and say, honestly, that no basis was recorded. A
 * SUBMISSION has no such history: P-FAB-05 checks the basis, so a submission
 * that omits it cannot be accepted, and the requirement is stated HERE — in the
 * type every consumer compiles against — rather than discovered by placing an
 * order and reading a 422.
 *
 * `deltaEBasis` is required-but-NULLABLE: he must state the basis he read the
 * acknowledgement on, and `null` is a legitimate statement (no basis recorded).
 * `API-REVIEW-GET` returns each acknowledgement in exactly this shape, so
 * building a submission is echoing what Review served, field for field.
 *
 * `text` is NOT here: the server authors it (see BACKEND_NOTES §2, D-2). Only
 * the things that are genuinely the client's — which acknowledgement, when, on
 * which cloth, on which basis, by which path — cross the wire inbound.
 */
export interface SubmittedAcknowledgement {
  readonly acknowledgementId: string;
  readonly version: number;
  readonly acceptedAt: string;
  /** [T-46b] — his statement of the cloth he was looking at. Checked by P-FAB-05. */
  readonly fabricId: string | null;
  /** The figure actually displayed to him. `null` whenever the basis is unmeasured. */
  readonly deltaEShown: number | null;
  /** REQUIRED. The cloth's colour basis as he read it. `null` is a valid answer. */
  readonly deltaEBasis: string | null;
  readonly pathChosen?: string | null;
}

/** FLOWS_SPEC §7.1. NO PRICE FIELD IS EVER SENT (P-ORD-02). */
export interface PlaceOrderRequest {
  readonly draftId: string;
  readonly quoteId: string;
  readonly configHash: string;
  readonly acknowledgements: readonly SubmittedAcknowledgement[];
  readonly deliveryAddress: string;
  readonly contact: string;
  readonly fittingPreference: string;
  readonly clientNote?: string;
}

export interface PlaceOrderResponse {
  readonly orderRef: string;
  readonly priceMinor: number;
  readonly currency: string;
  readonly placedAt: string;
}

export interface TransitionRequest {
  readonly event: OrderTransitionEvent;
  /** Conditional write guard. A mismatch is TRANSITION_CONFLICT (409). */
  readonly expectedFrom: OrderState;
  readonly note?: string;
  readonly payload?: Readonly<Record<string, unknown>>;
  readonly idempotencyKey: string;
}

export interface TransitionResponse {
  readonly state: OrderState;
  readonly allowedEvents: readonly OrderTransitionEvent[];
  readonly eventSeq: number;
}

export interface HaltPayload {
  readonly haltCode: HaltCode;
  readonly reason: string;
  readonly clientMessage?: string;
}

export interface PromiseRequest {
  readonly promisedWindow: PromisedWindow;
  /** Required. A date never slips silently (D-023). */
  readonly reason: string;
}

export interface StockMoveRequest {
  readonly boltId: string;
  /** SIGNED. There is no field anywhere that accepts a target on-hand quantity. */
  readonly deltaM: number;
  readonly reason: StockMovementReason;
  readonly note?: string;
}

export interface CreateMeasurementRecordRequest {
  readonly takenAt: string;
  readonly takenAtPlace: string;
  readonly method: string;
  readonly reason: string;
  readonly supersedes: string | null;
  readonly values: Readonly<Record<string, number | string>>;
  readonly notes?: string;
}
